// Page-session cache for the app-wide startup lookups (the user's company,
// the current user, the subscription / Stripe customer, ...).
//
// - Concurrent identical requests share one in-flight promise.
// - A successful result is kept for the page session (or `ttlMs`); a failure
//   is dropped, so the next call retries.
// - Keys are token + URL, so another session (another user, a refreshed
//   token) never sees an earlier answer.
// - Code that changes one of these resources calls invalidate(<url part>)
//   after the mutation succeeds; sign-in/sign-out call invalidate().
//
// Only for these lookups: list/data endpoints are not cached.

export type CacheOptions<T> = {
	/** how long a successful result stays fresh (ms); default: the page session */
	ttlMs?: number;
	/** skip a cached result and request again (the new result replaces it) */
	fresh?: boolean;
	/** whether a successful result may be kept (default: always) */
	cacheIf?: (value: T) => boolean;
};

type Entry = {
	promise: Promise<unknown>;
	settled: boolean;
	value?: unknown;
	expiresAt: number;
};

const store = new Map<string, Entry>();

export const requestKey = (token: string | null | undefined, url: string): string => `${token ?? ""} ${url}`;

const urlOf = (key: string) => key.slice(key.indexOf(" ") + 1);

const isFresh = (entry: Entry) => !entry.settled || entry.expiresAt > Date.now();

export function cachedRequest<T>(
	token: string | null | undefined,
	url: string,
	load: () => Promise<T>,
	{ ttlMs, fresh = false, cacheIf }: CacheOptions<T> = {}
): Promise<T> {
	const key = requestKey(token, url);
	const existing = store.get(key);
	if (existing && !fresh && isFresh(existing)) return existing.promise as Promise<T>;

	const entry: Entry = { promise: Promise.resolve(), settled: false, expiresAt: Number.POSITIVE_INFINITY };
	const drop = () => {
		// only this request's own entry (it may have been invalidated or replaced meanwhile)
		if (store.get(key) === entry) store.delete(key);
	};
	entry.promise = load().then(
		(value) => {
			if (cacheIf && !cacheIf(value)) {
				drop();
			} else if (store.get(key) === entry) {
				entry.settled = true;
				entry.value = value;
				entry.expiresAt = ttlMs === undefined ? Number.POSITIVE_INFINITY : Date.now() + ttlMs;
			}
			return value;
		},
		(error) => {
			drop();
			throw error;
		}
	);
	store.set(key, entry);
	return entry.promise as Promise<T>;
}

/** A settled, still-fresh result, synchronously (for a first render without a spinner). */
export function peekCachedRequest<T>(
	token: string | null | undefined,
	url: string
): { hit: true; value: T } | { hit: false } {
	const entry = store.get(requestKey(token, url));
	if (entry && entry.settled && isFresh(entry)) return { hit: true, value: entry.value as T };
	return { hit: false };
}

/**
 * Forget cached results whose URL contains `urlPart` (all of them when
 * omitted). A request still in flight settles for its callers but is not
 * kept.
 */
export function invalidate(urlPart?: string): void {
	if (urlPart === undefined) {
		store.clear();
		return;
	}
	Array.from(store.keys()).forEach((key) => {
		if (urlOf(key).includes(urlPart)) store.delete(key);
	});
}

export type CachedResponse<T = any> = { ok: boolean; status: number; data?: T };

/**
 * GET `url` with the bearer token through the cache, as { ok, status, data }.
 * Non-2xx answers are returned but not kept.
 */
export function cachedGetJson<T = any>(url: string, token: string): Promise<CachedResponse<T>> {
	return cachedRequest<CachedResponse<T>>(
		token,
		url,
		async () => {
			const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
			if (!res.ok) return { ok: false, status: res.status };
			return { ok: true, status: res.status, data: await res.json() };
		},
		{ cacheIf: (result) => result.ok }
	);
}

/** URL fragments of the shared lookups, for invalidate() */
export const CACHED = {
	userCompany: "/company?type=getUserCompany",
	currentUser: "/company?type=getCurrentUser",
	currentSubscription: "/payment?type=getCurrentSubscription",
	stripeCustomer: "/payment?type=getStripeCustomerData",
} as const;
